import type { ApiClientOptions, RequestOptions, TokenPair } from './types'
import { ApiError } from './errors'
import type { ApiErrorEnvelope } from './errors'

const DEFAULT_TIMEOUT_MS = 20_000
const DEFAULT_UPLOAD_TIMEOUT_MS = 30_000
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
  if (!entry) return null
  try {
    return decodeURIComponent(entry.slice(prefix.length))
  } catch {
    return null
  }
}

function buildQuery(
  query: RequestOptions['query'],
): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, rawValue] of Object.entries(query)) {
    const values = Array.isArray(rawValue) ? rawValue : [rawValue]
    for (const value of values) {
      if (value === undefined || value === null) continue
      params.append(key, String(value))
    }
  }
  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: string }).name === 'AbortError'
  )
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData
}

function isRawBody(
  body: unknown,
): body is BodyInit {
  return (
    typeof body === 'string' ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
    (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) ||
    (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
  )
}

interface TimeoutHandle {
  signal: AbortSignal
  timedOut: () => boolean
  cleanup: () => void
}

function withTimeout(
  externalSignal: AbortSignal | undefined,
  timeoutMs: number,
): TimeoutHandle {
  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)
  const abortFromOutside = () => controller.abort()
  if (externalSignal?.aborted) {
    controller.abort()
  } else {
    externalSignal?.addEventListener('abort', abortFromOutside, { once: true })
  }
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    cleanup: () => {
      clearTimeout(timer)
      externalSignal?.removeEventListener('abort', abortFromOutside)
    },
  }
}

/**
 * Typed HTTP boundary for the frozen `/api/v1` contract.
 *
 * - web uses same-origin cookies plus the double-submit CSRF header;
 * - native uses bearer tokens and a secure refresh-token store;
 * - one refresh is single-flight across concurrent 401 responses;
 * - every request has a 20s timeout (30s for multipart uploads);
 * - errors are normalized to `ApiError` for catalog-based UI copy.
 *
 * This is intentionally not a query cache (D-042). Route loaders/actions own
 * the request lifecycle and call this client directly.
 */
export class ApiClient {
  private readonly mode: ApiClientOptions['mode']
  private readonly baseUrl: string
  private readonly clientPlatform: NonNullable<ApiClientOptions['clientPlatform']>
  private readonly fetchImpl: typeof fetch
  private readonly getAccessToken: () => string | null
  private readonly getRefreshToken: () => string | null
  private readonly getLocale: () => string
  private readonly onTokensRefreshed?: (tokens: TokenPair) => void | Promise<void>
  private readonly onUnauthorized?: () => void
  private readonly timeoutMs: number
  private readonly uploadTimeoutMs: number
  private refreshPromise: Promise<void> | null = null

  constructor(options: ApiClientOptions) {
    this.mode = options.mode
    this.baseUrl = (options.baseUrl ?? '/api/v1').replace(/\/$/, '')
    this.clientPlatform = options.clientPlatform ?? 'web'
    this.fetchImpl =
      options.fetchImpl ??
      ((input, init) => globalThis.fetch(input, init))
    this.getAccessToken = options.getAccessToken ?? (() => null)
    this.getRefreshToken = options.getRefreshToken ?? (() => null)
    this.getLocale = options.getLocale ?? (() => 'it')
    this.onTokensRefreshed = options.onTokensRefreshed
    this.onUnauthorized = options.onUnauthorized
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
    this.uploadTimeoutMs = options.uploadTimeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS
  }

  get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return this.request<T>(path, { ...options, method: 'GET' })
  }

  post<T>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method' | 'body'> = {},
  ) {
    return this.request<T>(path, { ...options, method: 'POST', body })
  }

  patch<T>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method' | 'body'> = {},
  ) {
    return this.request<T>(path, { ...options, method: 'PATCH', body })
  }

  delete<T>(
    path: string,
    options: Omit<RequestOptions, 'method' | 'body'> = {},
  ) {
    return this.request<T>(path, { ...options, method: 'DELETE' })
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.perform(path, options)

    if (response.status === 401 && this.canAttemptRefresh(path, options)) {
      await this.refreshSingleFlight()
      const retried = await this.perform(path, options)
      if (retried.status === 401) this.onUnauthorized?.()
      return this.parse<T>(retried)
    }

    if (
      response.status === 401 &&
      !options.skipAuth &&
      !path.startsWith('/auth/')
    ) {
      this.onUnauthorized?.()
    }

    return this.parse<T>(response)
  }

  private canAttemptRefresh(path: string, options: RequestOptions): boolean {
    if (options.skipAuth || options.skipRefresh) return false
    if (path.startsWith('/auth/')) return false
    return this.mode === 'cookie' || Boolean(this.getRefreshToken())
  }

  private buildUrl(path: string, query: RequestOptions['query']): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return `${this.baseUrl}${normalizedPath}${buildQuery(query)}`
  }

  private buildHeaders(
    options: RequestOptions,
    hasJsonBody: boolean,
    isFormBody: boolean,
    method: string,
  ): Headers {
    const headers = new Headers(options.headers)
    if (!headers.has('Accept')) headers.set('Accept', 'application/json')
    headers.set('X-Client-Platform', this.clientPlatform)
    if (!headers.has('Accept-Language')) {
      headers.set('Accept-Language', this.getLocale())
    }

    if (hasJsonBody && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    if (isFormBody && headers.has('Content-Type')) {
      // The browser must add the multipart boundary itself.
      headers.delete('Content-Type')
    }

    if (!options.skipAuth) {
      const accessToken = this.getAccessToken()
      if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
    }

    if (this.mode === 'cookie' && MUTATING_METHODS.has(method)) {
      const csrfToken = readCookie('kuniri_csrf')
      if (csrfToken) headers.set('X-CSRF-Token', csrfToken)
    }

    return headers
  }

  private async perform(
    path: string,
    options: RequestOptions,
  ): Promise<Response> {
    const method = (options.method ?? 'GET').toUpperCase()
    if (
      method !== 'GET' &&
      method !== 'HEAD' &&
      typeof navigator !== 'undefined' &&
      navigator.onLine === false
    ) {
      throw new ApiError('OFFLINE', 'The browser is offline')
    }

    const isFormBody = isFormData(options.body)
    const hasJsonBody =
      options.body !== undefined &&
      options.body !== null &&
      !isFormBody &&
      !isRawBody(options.body)

    let body: BodyInit | undefined
    if (options.body === undefined || options.body === null) {
      body = undefined
    } else if (isFormBody || isRawBody(options.body)) {
      body = options.body as BodyInit
    } else {
      body = JSON.stringify(options.body)
    }

    const headers = this.buildHeaders(options, hasJsonBody, isFormBody, method)
    return this.fetchWithTimeout(
      this.buildUrl(path, options.query),
      {
        method,
        headers,
        body,
        credentials:
          options.credentials ??
          (this.mode === 'cookie' ? 'include' : 'omit'),
      },
      options.timeoutMs ?? (isFormBody ? this.uploadTimeoutMs : this.timeoutMs),
      options.signal,
    )
  }

  private async fetchWithTimeout(
    url: string,
    init: RequestInit,
    timeoutMs: number,
    externalSignal: AbortSignal | undefined,
  ): Promise<Response> {
    const timeout = withTimeout(externalSignal, timeoutMs)
    try {
      return await this.fetchImpl(url, { ...init, signal: timeout.signal })
    } catch (error) {
      if (isAbortError(error)) {
        throw new ApiError(
          timeout.timedOut() ? 'TIMEOUT' : 'ABORTED',
          timeout.timedOut()
            ? 'The request timed out'
            : 'The request was aborted',
          { cause: error },
        )
      }
      if (error instanceof ApiError) throw error
      throw new ApiError('NETWORK_ERROR', 'Network request failed', {
        cause: error,
      })
    } finally {
      timeout.cleanup()
    }
  }

  private async parse<T>(response: Response): Promise<T> {
    const requestId = response.headers.get('x-request-id')
    if (!response.ok) {
      let envelope: ApiErrorEnvelope | null = null
      try {
        envelope = (await response.json()) as ApiErrorEnvelope
      } catch {
        envelope = null
      }
      throw ApiError.fromEnvelope(envelope, response.status, requestId)
    }

    if (response.status === 204) return undefined as T

    const text = await response.text()
    if (!text) return undefined as T
    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('application/json')) {
      try {
        return JSON.parse(text) as T
      } catch {
        throw new ApiError('INTERNAL_ERROR', 'Invalid JSON response', {
          status: response.status,
          requestId,
        })
      }
    }
    return text as T
  }

  private refreshSingleFlight(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.refresh().finally(() => {
        this.refreshPromise = null
      })
    }
    return this.refreshPromise
  }

  private async refresh(): Promise<void> {
    const refreshToken = this.getRefreshToken()
    if (this.mode === 'bearer' && !refreshToken) {
      this.onUnauthorized?.()
      throw new ApiError('UNAUTHORIZED', 'No refresh token available', {
        status: 401,
      })
    }

    const headers = new Headers({
      Accept: 'application/json',
      'Accept-Language': this.getLocale(),
      'Content-Type': 'application/json',
      'X-Client-Platform': this.clientPlatform,
    })
    if (this.mode === 'cookie') {
      const csrfToken = readCookie('kuniri_csrf')
      if (csrfToken) headers.set('X-CSRF-Token', csrfToken)
    }

    // Transient failures must not destroy a usable local session.
    const response = await this.fetchWithTimeout(
      this.buildUrl('/auth/refresh', undefined),
      {
        method: 'POST',
        headers,
        credentials: this.mode === 'cookie' ? 'include' : 'omit',
        body: JSON.stringify(this.mode === 'bearer' ? { refreshToken } : {}),
      },
      this.timeoutMs,
      undefined,
    )

    if (response.status === 401 || response.status === 403) {
      this.onUnauthorized?.()
      let envelope: ApiErrorEnvelope | null = null
      try {
        envelope = (await response.json()) as ApiErrorEnvelope
      } catch {
        envelope = null
      }
      throw ApiError.fromEnvelope(
        envelope,
        response.status,
        response.headers.get('x-request-id'),
      )
    }

    if (!response.ok) {
      let envelope: ApiErrorEnvelope | null = null
      try {
        envelope = (await response.json()) as ApiErrorEnvelope
      } catch {
        envelope = null
      }
      throw ApiError.fromEnvelope(
        envelope,
        response.status,
        response.headers.get('x-request-id'),
      )
    }

    let tokens: TokenPair
    try {
      tokens = (await response.json()) as TokenPair
    } catch (error) {
      throw new ApiError('INTERNAL_ERROR', 'Invalid refresh response', {
        status: response.status,
        cause: error,
      })
    }
    if (!tokens.accessToken) {
      throw new ApiError('INTERNAL_ERROR', 'Refresh response has no token', {
        status: response.status,
      })
    }
    await this.onTokensRefreshed?.(tokens)
  }
}
